import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  TablePagination,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import RichTextEditor from "@crema/utility/RichTextEditor";
import AppConfirmDialog from "@crema/core/AppConfirmDialog";
import { AppState } from "redux/store";
import {
  getInvestorDeskSections,
  updateInvestorDeskSection,
  getReportTypes,
  getReportYears,
  getFinancialDocumentsByType,
  getFinancialDocumentsByTypeAndYear,
  deleteFinancialDocument,
} from "redux/actions";
import {
  ReportType,
  ReportYear,
  InvestorDeskReport,
} from "types/models/investorDeskMaster/InvestorDeskMasterApp";
import UploadDocumentDialog from "./UploadDocumentDialog";

const DocumentsPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { sections, reportTypes, reportYears, documents } = useSelector<
    AppState,
    AppState["investorDeskMaster"]
  >(({ investorDeskMaster }) => investorDeskMaster);

  // Section 1: Heading & Content
  const [sectionId, setSectionId] = useState<number | null>(null);
  const [sectionTitle, setSectionTitle] = useState("Documents");
  const [heading, setHeading] = useState("");
  const [content, setContent] = useState("");
  const [isLoadingSection, setIsLoadingSection] = useState(false);
  const [isSavingSection, setIsSavingSection] = useState(false);

  // Section 2: Filters & Documents
  // Default to report type 15 ("Code of Conduct" / Documents used by website Documents tab)
  const [selectedTypeId, setSelectedTypeId] = useState<number>(15);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [filteredDocuments, setFilteredDocuments] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Dialog & Notifications
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Load initial data
  useEffect(() => {
    setIsLoadingSection(true);
    dispatch(getInvestorDeskSections());
    dispatch(getReportTypes());
    dispatch(getReportYears());
  }, [dispatch]);

  // Find Documents section from sections list
  useEffect(() => {
    if (sections && sections.length > 0) {
      setIsLoadingSection(false);
      const matched = sections.find((item: InvestorDeskReport) => {
        const t = (item?.title || "").toLowerCase();
        return t.includes("document");
      });

      if (matched) {
        setSectionId(matched.id);
        setSectionTitle(matched.title || "Documents");
        setHeading(matched.heading || "");
        setContent(matched.content || "");
      }
    }
  }, [sections]);

  // Set default report type if 15 is not present or when types load
  useEffect(() => {
    if (reportTypes && reportTypes.length > 0) {
      const has15 = reportTypes.some((t) => t.id === 15);
      if (!has15 && !selectedTypeId) {
        setSelectedTypeId(reportTypes[0].id);
      }
    }
  }, [reportTypes, selectedTypeId]);

  // Trigger search
  const executeSearch = (typeId?: number, yearVal?: string) => {
    const targetTypeId = typeId || selectedTypeId;
    const targetYear = yearVal !== undefined ? yearVal : selectedYear;

    if (!targetTypeId) {
      setSnackbar({
        open: true,
        message: "Please select a report type",
        severity: "error",
      });
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    const action = targetYear
      ? getFinancialDocumentsByTypeAndYear(targetTypeId, targetYear)
      : getFinancialDocumentsByType(targetTypeId);

    dispatch(action as any);
  };

  // Run initial search for documents once types load
  useEffect(() => {
    if (selectedTypeId) {
      executeSearch(selectedTypeId, selectedYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTypeId]);

  // Sync documents from redux store
  useEffect(() => {
    if (documents) {
      setFilteredDocuments(documents);
      setIsSearching(false);
    }
  }, [documents]);

  // Handle section content submit
  const handleSectionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!heading.trim() || !content.trim()) {
      setSnackbar({
        open: true,
        message: "Please fill in heading and content",
        severity: "error",
      });
      return;
    }

    if (!sectionId) {
      setSnackbar({
        open: true,
        message: "Documents section not found to update",
        severity: "error",
      });
      return;
    }

    setIsSavingSection(true);
    try {
      await dispatch(
        updateInvestorDeskSection(sectionId, {
          title: sectionTitle || "Documents",
          heading,
          content,
          status: "active",
        }) as any,
      );
      setSnackbar({
        open: true,
        message: "Documents content updated successfully",
        severity: "success",
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || "Failed to update documents content",
        severity: "error",
      });
    } finally {
      setIsSavingSection(false);
    }
  };

  const handleDownload = (file: any) => {
    if (file.file_url) {
      window.open(file.file_url, "_blank");
    } else {
      setSnackbar({
        open: true,
        message: "File URL is not available",
        severity: "error",
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await dispatch(deleteFinancialDocument(deleteId) as any);
      setSnackbar({
        open: true,
        message: "Document deleted successfully",
        severity: "success",
      });
      executeSearch();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || "Failed to delete document",
        severity: "error",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const paginatedDocs = filteredDocuments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 50,
              height: 50,
              borderRadius: "12px",
              color: "primary.main",
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.4rem", md: "1.75rem" },
                letterSpacing: "-0.02em",
              }}
            >
              Edit Documents
            </Typography>
            <Typography variant="body2">
              Update documents content and manage files
            </Typography>
          </Box>
        </Box>
      </Box>
      {/* Main Container Card */}
      <Card
        sx={{
          borderRadius: 4,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          {/* Section 1: Heading & Content Accordion */}
          <form onSubmit={handleSectionSubmit}>
            <Accordion
              defaultExpanded
              disableGutters
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "12px !important",
                "&:before": { display: "none" },
                overflow: "hidden",
                mb: 4,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: "#fafafa",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  px: 3,
                  py: 1,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                    Documents Content
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Heading and content for documents
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ p: { xs: 2.5, md: 4 } }}>
                {isLoadingSection ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 3.5,
                      }}
                    >
                      {/* Heading */}
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            mb: 1,
                            color: "#374151",
                          }}
                        >
                          Heading <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <Box
                          sx={{
                            border: "1px solid #d1d5db",
                            borderRadius: 2,
                            overflow: "hidden",
                            bgcolor: "#fff",
                            "& .quill": { height: "auto" },
                            "& .ql-container": {
                              minHeight: "80px",
                              fontSize: 14,
                            },
                          }}
                        >
                          <RichTextEditor
                            value={heading}
                            onChange={(val) => setHeading(val)}
                          />
                        </Box>
                      </Box>

                      {/* Content */}
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            mb: 1,
                            color: "#374151",
                          }}
                        >
                          Content <span style={{ color: "#ef4444" }}>*</span>
                        </Typography>
                        <Box
                          sx={{
                            border: "1px solid #d1d5db",
                            borderRadius: 2,
                            overflow: "hidden",
                            bgcolor: "#fff",
                            "& .quill": { height: "auto" },
                            "& .ql-container": {
                              minHeight: "160px",
                              fontSize: 14,
                            },
                          }}
                        >
                          <RichTextEditor
                            value={content}
                            onChange={(val) => setContent(val)}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {/* Actions */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 2,
                        mt: 4,
                        pt: 3,
                        borderTop: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={() => navigate(-1)}
                        disabled={isSavingSection}
                        sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSavingSection}
                        startIcon={
                          isSavingSection ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        sx={{
                          textTransform: "none",
                          borderRadius: 2,
                          px: 3.5,
                          fontWeight: 600,
                        }}
                      >
                        {isSavingSection ? "Updating..." : "Update"}
                      </Button>
                    </Box>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          </form>

          {/* Section 2: Document Management */}
          <Box
            sx={{
              mt: 5,
              pt: 4,
              borderTop: "2px solid",
              borderColor: "#f3f4f6",
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{ fontSize: 18, fontWeight: 700, color: "text.primary" }}
              >
                Documents Management
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                Manage uploaded documents for the investor desk
              </Typography>
            </Box>

            {/* Filter Bar */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2,
                p: 3,
                bgcolor: "#f9fafb",
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                mb: 4,
              }}
            >
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={selectedTypeId}
                  label="Report Type"
                  onChange={(e) => {
                    const newId = Number(e.target.value);
                    setSelectedTypeId(newId);
                    executeSearch(newId, selectedYear);
                  }}
                >
                  {reportTypes.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Select Year</InputLabel>
                <Select
                  value={selectedYear}
                  label="Select Year"
                  onChange={(e) => {
                    const y = e.target.value;
                    setSelectedYear(y);
                    executeSearch(selectedTypeId, y);
                  }}
                >
                  <MenuItem value="">All Years</MenuItem>
                  {reportYears.map((y) => (
                    <MenuItem key={y.id} value={y.year}>
                      {y.year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: "flex", gap: 1.5, ml: "auto" }}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={
                    isSearching ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  onClick={() => executeSearch()}
                  disabled={isSearching}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    px: 2.5,
                    fontWeight: 600,
                  }}
                >
                  Search
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenUploadDialog(true)}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    px: 2.5,
                    fontWeight: 600,
                  }}
                >
                  Add File
                </Button>
              </Box>
            </Box>

            {/* Table of Uploaded Documents */}
            {(hasSearched || filteredDocuments.length > 0) && (
              <Box sx={{ mt: 4 }}>
                <Typography
                  sx={{
                    fontSize: 17,
                    fontWeight: 700,
                    mb: 2,
                    color: "text.primary",
                  }}
                >
                  Uploaded Files
                </Typography>

                {filteredDocuments.length === 0 ? (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      bgcolor: "#f9fafb",
                      borderRadius: 2,
                      border: "1px dashed #d1d5db",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      No files uploaded yet for this selection.
                    </Typography>
                  </Box>
                ) : (
                  <Card
                    sx={{
                      borderRadius: 2.5,
                      border: "1px solid #e5e7eb",
                      boxShadow: "none",
                      overflow: "hidden",
                    }}
                  >
                    <Table>
                      <TableHead sx={{ bgcolor: "#f9fafb" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600, color: "#4b5563" }}>
                            Document Title
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#4b5563" }}>
                            Year
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#4b5563" }}>
                            File Type
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 600, color: "#4b5563" }}
                          >
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedDocs.map((doc) => (
                          <TableRow key={doc.id} hover>
                            <TableCell
                              sx={{ fontWeight: 500, color: "#111827" }}
                            >
                              {doc.document_title || doc.title}
                            </TableCell>
                            <TableCell sx={{ color: "#4b5563" }}>
                              {doc.report_year}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={
                                  doc.mime_type
                                    ? doc.mime_type.split("/")[1] ||
                                      doc.mime_type
                                    : "file"
                                }
                                sx={{
                                  borderRadius: 1,
                                  fontSize: 12,
                                  bgcolor: "#e0f2fe",
                                  color: "#0369a1",
                                  fontWeight: 500,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  gap: 1,
                                }}
                              >
                                <Tooltip title="Download File">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleDownload(doc)}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete File">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setDeleteId(doc.id)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <TablePagination
                      component="div"
                      count={filteredDocuments.length}
                      page={page}
                      onPageChange={(_, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                      rowsPerPageOptions={[5, 10, 25]}
                    />
                  </Card>
                )}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Add Document Dialog */}
      <UploadDocumentDialog
        open={openUploadDialog}
        onClose={() => setOpenUploadDialog(false)}
        onSuccess={() => {
          setSnackbar({
            open: true,
            message: "File(s) uploaded successfully",
            severity: "success",
          });
          executeSearch();
        }}
        investorDeskId={sectionId}
        reportTypes={reportTypes}
        reportYears={reportYears}
        defaultReportTypeId={selectedTypeId}
        defaultYear={selectedYear}
      />

      {/* Delete Confirmation Dialog */}
      <AppConfirmDialog
        open={deleteId != null}
        onDeny={() => setDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title="Delete File"
        dialogTitle="Delete File"
      />

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentsPage;
